import { ISizeRuns } from '@epicuro-next/data/product';
import { ProductListSource } from '@epicuro-next/data/stories';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { ExtendedBasketAccountAddress } from '@epicuro-next/state/basket-state';
import { createAction, props } from '@ngrx/store';
import {
  BasketAccountAddress,
  Product,
  ProductKey,
} from 'graphql-api/gql-api-interfaces';

import {
  BackgroundProductState,
  CachedState,
  DataState,
  DisplayByMode,
  MemoProductSizes,
  ProductPriceData,
  ProductQuantitiesChange,
  ProductSizeEntities,
  ProductSourceDetails,
  ProductType,
  ProductVariant,
  UiEcommerceTrackingSource,
} from '../models';

export const setConfigStripIsOpen = createAction(
  '[Config Strip] Set Open',
  props<{ open: boolean }>(),
);

/**
 * Makes a GQL request to fetch the product
 */
export const loadProduct = createAction(
  '[Config Strip] Load Product',
  props<{ productId: string; brandId: string; variantId?: string }>(),
);

export const cachedProductResolved = createAction(
  '[Config Strip] Load Cached Product Success',
  props<{ product: Product }>(),
);

export const loadProductSuccess = createAction(
  '[Config Strip] Load Product Success',
  props<{ product: ProductType }>(),
);

export const loadProductFailure = createAction(
  '[Config Strip] Load Product Failure',
  props<{ error: string }>(),
);

export const loadPrices = createAction(
  '[Config Strip] Load Prices',
  props<{ products: ProductKey[] }>(),
);

export const loadPricesSuccess = createAction(
  '[Config Strip] Load Prices Success',
  props<{ prices: ProductPriceData }>(),
);

export const loadPricesFailure = createAction(
  '[Config Strip] Load Prices Failure',
  props<{ error: string }>(),
);

/**
 * Needs only for ECCO and should be retired eventually
 * Updates products with discounts
 */
export const updatePricesWithDiscounts = createAction(
  '[Config Strip] Update Prices with discounts',
  props<{ prices: ProductPriceData }>(),
);

export const selectVariant = createAction(
  '[Config Strip] Select Product Variant',
  props<{ productId: string }>(),
);

export const selectSize = createAction(
  '[Config Strip] Select Product Size',
  props<{ sizeId: string; variantId: string | null }>(),
);

export const selectPrepack = createAction(
  '[Config Strip] Select Prepack',
  props<{ productId: string }>(),
);

export const copyQuantities = createAction(
  '[Config Strip] Copy quantities',
  props<{
    from: {
      productId: string;
      shopId: string;
    };
    to: {
      shops: string[];
      products: string[];
    };
    /** current data state, so products are set correctly (inventories, prices, etc) */
    data: DataState;
  }>(),
);

export const setDeliveryShops = createAction(
  '[Config Strip] Set Delivery Shops',
  props<{ deliveryShops: ExtendedBasketAccountAddress[] }>(),
);

export const setActiveDeliveryShop = createAction(
  '[Config Strip] Set Active Delivery Shop',
  props<{ accountId: string; addressId: string }>(),
);

export const setProductQuantity = createAction(
  '[Config Strip] Set Product Quantity',
  props<{
    changes: { productId: string; quantity: number }[];
    /** if delivery shop provided than items will be added to provided shop */
    deliveryShop?: BasketAccountAddress;
    /** current data state, so products are set correctly (inventories, prices, etc) */
    data: DataState;
  }>(),
);

export const setPrefilledVisible = createAction(
  '[Config Strip] Set Prefilled Visible Delivery Items',
  props<{ visible: boolean }>(),
);

export const setMasterPrefilledDeliveryItems = createAction(
  '[Config Strip] Set Master Prefilled Delivery Items Quantity',
  props<{ variant: ProductVariant; scaleField: string; sizeField: string }>(),
);

export const setPrefilledDeliveryItems = createAction(
  '[Config Strip] Set Prefilled Delivery',
  props<{
    category: string;
    scaleField: string;
    sizeField: string;
    sizes: ProductSizeEntities;
  }>(),
);

export const clearPrefilledDeliveryItems = createAction(
  '[Config Strip] Clear Prefilled Delivery',
);

export const copyPrefilledDeliveryItems = createAction(
  '[Config Strip] Copy Prefilled Delivery to Delivery Items',
  props<{
    periods: string[];
    shops: ExtendedBasketAccountAddress[];
    variants: ProductVariant[];
    scaleField: string;
    sizeField: string;
    /** current data state, so can be copied taking into account inv, prices restrictions */
    data: DataState;
  }>(),
);

export const setProductNote = createAction(
  '[Config Strip] Set Product Note',
  props<{ productId: string; note: string }>(),
);

export const clearState = createAction('[Config Strip] Clear state');

export const persistChanges = createAction('[Config Strip] Persist changes');

export const persistChangesSuccess = createAction(
  '[Config Strip] Persist Changes Success',
);

export const persistChangesFailure = createAction(
  '[Config Strip] Persist Changes Failure',
  props<{ error: string }>(),
);

export const setProductQuantities = createAction(
  '[Config Strip] Set Product Quantities',
  props<{
    changes: ProductQuantitiesChange[];
    /** current data state, so products are set correctly (inventories, prices, etc) */
    data: DataState;
  }>(),
);

export const selectScale = createAction(
  '[Config Strip] Select scale',
  props<{ scale: string }>(),
);

/** under the hood it inefficiently uses reducer of setProductQuantities actions */
export const copyToShops = createAction(
  '[Config Strip] Copy to shop',
  props<{
    fromShopId: string;
    toShopIds: string[];
    /** current data state, so products are set correctly (inventories, prices, etc) */
    data: DataState;
  }>(),
);

export const clearShopQuantities = createAction(
  '[Config Strip] Clear shop quantities',
  props<{ shopId: string; data: DataState }>(),
);

export const copyToDeliveryPeriod = createAction(
  '[Config Strip] Copy to delivery period',
  props<{
    fromDeliveryPeriod: string;
    toDeliveryPeriods: string[];
    allShops?: boolean;
    data: DataState;
  }>(),
);

export const clearShopQuantitiesSuccess = createAction(
  '[Config Strip] Clear shop quantities success',
);

export const resetPasteStatus = createAction(
  '[Config Strip] Reset Paste Status',
);

export const preSelectDisplayByMode = createAction(
  '[Config Strip] Pre-Select Display Mode',
  props<{ preSelectDisplayByMode: DisplayByMode }>(),
);

export const setDisplayByMode = createAction(
  '[Config Strip] Set Display By Mode',
  props<{ displayByMode: DisplayByMode }>(),
);

export const setValidators = createAction(
  '[Config Strip] Set validators',
  props<{ validators: string[] }>(),
);

export const validateDeliveryItems = createAction(
  '[Config Strip] Validate delivery items',
  props<{ validating: boolean }>(),
);

export const validateDeliveryItemsSuccess = createAction(
  '[Config Strip] Validate delivery items success',
  props<{
    validationErrors: string[];
    deliveryPeriod: string;
    deliveryShop: string;
  }>(),
);

export const openCSForPBasket = createAction(
  '[Config Strip] Open CS for P-Basket',
);

export const openCSForList = createAction('[Config Strip] Open CS for List');

export const openCSForLook = createAction('[Config Strip] Open CS for Look');

export const openCSFromCbasket = createAction(
  '[Config Strip] Open CS from C-Basket',
);

export const setBackgroundProducts = createAction(
  '[Config Strip] Set Background Products',
  props<{ backgroundProducts: BackgroundProductState[] }>(),
);

export const setNextButtonVisible = createAction(
  '[Config Strip] Set next button visibility',
  props<{ showButton: boolean }>(),
);

export const setSkipInventoryCheck = createAction(
  '[Config Strip] Set skip inventory check',
  props<{ skipInventoryCheck: boolean }>(),
);

export const updateCache = createAction(
  '[Config Strip] Update Cache',
  props<CachedState>(),
);

export const addButtonClickStrategyHandle = createAction(
  '[Config Strip] Handle CS overlay',
  props<{ navigateToNext: boolean }>(),
);

export const goToNextProduct = createAction(
  '[Config Strip] Go to the next product in the config strip',
);

export const memoizeProductSizeData = createAction(
  '[Config Strip] Memoize Product Size Data',
  props<
    {
      articleId: string;
      brandId: string;
    } & MemoProductSizes
  >(),
);

export const setProductSource = createAction(
  '[Config Strip] Set product source',
  props<{
    // tslint:disable-next-line:max-union-size
    source: ProductListSource | BasketItemSource;
    details?: ProductSourceDetails;
    additionalUiDetails?: ProductSourceDetails;
  }>(),
);

export const setActiveDeliveryShops = createAction(
  '[Config Strip] Set active delivery shops',
  props<{ shops: { accountId: string; addressId: string }[] }>(),
);

export const setEcommerceTrackingSource = createAction(
  '[Config Strip] Set Ecommerce Tracking Source',
  props<{ data: UiEcommerceTrackingSource | null }>(),
);

export const loadSizeRuns = createAction(
  '[Config Strip] Load Size Runs',
  props<{ product: ProductType }>(),
);

export const loadSizeRunsSuccess = createAction(
  '[Config Strip] Load Size Runs Success',
  props<{ sizeRuns: ISizeRuns }>(),
);
